/**
 * IMP-2558: login and password-reset requests that never finish, or fail with no message.
 *
 * axiosClient waits up to 10 minutes for a response. On a school network that stalls a request,
 * the Log In or Submit button spun for that long, and students read it as a frozen page. A 429
 * from the per-IP rate limit (one school IP serves the whole classroom) came back fast, but the
 * login page showed only red field outlines and the forgot-password page showed nothing.
 *
 * Auth calls now pass AUTH_REQUEST_TIMEOUT_MS, and these two failure shapes map to fixed copy.
 * Pages show the copy inline in a role="alert" element. A toast alone disappears after
 * 3 seconds and is not announced by screen readers (WCAG 2.1 4.1.3 and 2.2.1).
 */
import { retryAfterSeconds, waitPhrase } from './registrationLinkError';

/** Upper bound for one login or password-reset request. The backend answers in a few seconds. */
export const AUTH_REQUEST_TIMEOUT_MS = 30000;

/**
 * Fixed copy for a rejected auth request, or undefined when the failure is not one of the
 * shapes handled here (the caller keeps its existing behaviour for those).
 *
 * The 429 copy stays neutral for the same reason as registrationLinkError.js: the backend sends
 * the same 429 whether one account or the whole network used up the attempts.
 */
export function authRequestErrorMessage(err) {
  if (err?.response?.status === 429) {
    return `Too many attempts right now. Please wait ${waitPhrase(retryAfterSeconds(err))}, then try again.`;
  }
  // Only an axios error with no response is a transport failure. An exception thrown by our own
  // code inside .then (e.g. navigation) also has no response, but is not the network's fault.
  if (err?.isAxiosError && !err.response) {
    return "We couldn't reach iMentor. Check your internet connection, then try again.";
  }
  return undefined;
}
